/**
 * Avatars Module
 *
 * Upload, list, and manage user avatars.
 * Avatars are required for submitting try-on requests.
 */

import type { Avatar, AvatarUploadOptions, MeasurementData, TryStyleEvents } from '../types';
import type { Fetcher } from '../utils/fetcher';
import type { EventEmitter } from '../utils/emitter';

interface ApiListResponse {
  success: boolean;
  data: {
    avatars: Avatar[];
  };
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface ApiAvatarResponse {
  success: boolean;
  data: Avatar;
}

interface ApiMeasurementResponse {
  success: boolean;
  data: {
    id: string;
    sourceAvatarId: string;
    measurements: MeasurementData;
    isManuallyAdjusted: boolean;
    createdAt: string;
    updatedAt: string;
  } | null;
}

export class AvatarsModule {
  constructor(
    private readonly fetcher: Fetcher,
    private readonly emitter: EventEmitter<TryStyleEvents>,
  ) {}

  /**
   * Upload a new avatar photo.
   * Measurements are automatically computed by the backend (PBA).
   *
   * @param file - The photo File object (PNG/JPG, up to 10 MB)
   * @param opts - Height in cm, gender, and optional isPrimary flag
   */
  async upload(file: File, opts: AvatarUploadOptions): Promise<Avatar> {
    const formData = new FormData();
    formData.append('avatar', file);
    formData.append('userRealHeightCm', String(opts.heightCm));
    formData.append('gender', opts.gender ?? 'female');
    if (opts.isPrimary !== undefined) {
      formData.append('isPrimary', String(opts.isPrimary));
    }

    const res = await this.fetcher.request<ApiAvatarResponse>(
      '/api/v1/auth/tryon/avatars',
      { method: 'POST', formData },
    );

    return res.data;
  }

  /**
   * List all avatars for the current user.
   */
  async list(page = 1, limit = 20): Promise<{ avatars: Avatar[]; total: number; totalPages: number }> {
    const res = await this.fetcher.request<ApiListResponse>(
      `/api/v1/auth/tryon/avatars?page=${page}&limit=${limit}`,
    );
    return {
      avatars: res.data.avatars,
      total: res.pagination?.total ?? res.data.avatars.length,
      totalPages: res.pagination?.totalPages ?? 1,
    };
  }

  /**
   * Get a single avatar by ID.
   */
  async get(avatarId: string): Promise<Avatar> {
    const res = await this.fetcher.request<ApiAvatarResponse>(
      `/api/v1/auth/tryon/avatars/${avatarId}`,
    );
    return res.data;
  }

  /**
   * Delete an avatar. Cannot delete your only avatar if it's being used.
   */
  async delete(avatarId: string): Promise<void> {
    await this.fetcher.request(`/api/v1/auth/tryon/avatars/${avatarId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Set an avatar as the primary (default) avatar.
   */
  async setPrimary(avatarId: string): Promise<Avatar> {
    const res = await this.fetcher.request<ApiAvatarResponse>(
      `/api/v1/auth/tryon/avatars/${avatarId}/primary`,
      { method: 'PUT' },
    );
    return res.data;
  }

  /**
   * Get measurements for a specific avatar.
   */
  async getMeasurements(avatarId: string): Promise<ApiMeasurementResponse['data']> {
    const res = await this.fetcher.request<ApiMeasurementResponse>(
      `/api/v1/auth/tryon/avatars/${avatarId}/measurements`,
    );
    return res.data;
  }

  /**
   * Download avatar image bytes through the API (CORS-safe).
   *
   * Best-effort mirror of the try-on result-image proxy for the processing scan
   * canvas. Note: no avatar-bytes route is currently documented on the backend,
   * so this is expected to fail until one ships — the UI sweep engine already
   * degrades to a plain laser/tint scan over the avatar's signedUrl.
   */
  async downloadImageBlob(avatarId: string): Promise<Blob> {
    return this.fetcher.requestBlob(
      `/api/v1/auth/tryon/avatars/${encodeURIComponent(avatarId)}/image`,
    );
  }
}
